import React, { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import { api, V1 } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sparkles, Save } from "lucide-react";
import { toast } from "sonner";
import { turkeyData, provinces } from "@/data/turkeyData";

export default function SchoolProfilePage() {
  const { user } = useAuth();
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user?.school_id) return;
    api.get(`${V1}/schools/${user.school_id}`).then((r) => setS(r.data));
  }, [user]);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch(`${V1}/schools/${s.id}`, {
        name: s.name, short_intro: s.short_intro, description: s.description,
        phone: s.phone, email: s.email, website: s.website, address: s.address,
        province: s.province, district: s.district
      });
      setS(data);
      toast.success("Kaydedildi");
    } catch { toast.error("Kaydedilemedi"); } finally { setBusy(false); }
  };

  const genAI = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`${V1}/ai/school-profile`, {
        school_id: s.id, short_intro: s.short_intro,
      });
      setS((prev) => ({ ...prev, ai_generated_content: data.content }));
      toast.success("AI içerik üretildi");
    } catch { toast.error("AI içerik üretilemedi"); } finally { setBusy(false); }
  };

  if (!s) return <div className="text-muted-foreground">Yükleniyor…</div>;

  return (
    <div className="space-y-6" data-testid="school-profile-page">
      <div>
        <div className="overline text-accent">Profil Yönetimi</div>
        <h1 className="font-display text-3xl font-bold">Okul Profili</h1>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <Label>Okul Adı</Label>
          <Input value={s.name || ""} onChange={(e) => setS({ ...s, name: e.target.value })} data-testid="school-name" />
        </div>
        <div>
          <Label>Telefon</Label>
          <Input value={s.phone || ""} onChange={(e) => setS({ ...s, phone: e.target.value })} />
        </div>
        <div>
          <Label>E-posta</Label>
          <Input value={s.email || ""} onChange={(e) => setS({ ...s, email: e.target.value })} />
        </div>
        <div>
          <Label>Web Sitesi</Label>
          <Input value={s.website || ""} onChange={(e) => setS({ ...s, website: e.target.value })} />
        </div>
        <div>
          <Label>İl</Label>
          <Select value={s.province || ""} onValueChange={(v) => setS({ ...s, province: v, district: "" })}>
            <SelectTrigger><SelectValue placeholder="İl Seçiniz" /></SelectTrigger>
            <SelectContent>
              {provinces.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>İlçe</Label>
          <Select value={s.district || ""} onValueChange={(v) => setS({ ...s, district: v })} disabled={!s.province}>
            <SelectTrigger><SelectValue placeholder="İlçe Seçiniz" /></SelectTrigger>
            <SelectContent>
              {s.province && turkeyData[s.province] ? turkeyData[s.province].map((d) => (
                <SelectItem key={d} value={d}>{d}</SelectItem>
              )) : null}
            </SelectContent>
          </Select>
        </div>
        <div className="md:col-span-2">
          <Label>Adres</Label>
          <Input value={s.address || ""} onChange={(e) => setS({ ...s, address: e.target.value })} />
        </div>
        <div className="md:col-span-2">
          <Label>Kısa Tanıtım (AI için)</Label>
          <Textarea rows={4} value={s.short_intro || ""} onChange={(e) => setS({ ...s, short_intro: e.target.value })} placeholder="Okulunuzu 2-3 cümlede anlatın. AI bunu detaylandıracak." data-testid="school-short-intro" />
        </div>
        <div className="md:col-span-2 flex gap-2">
          <Button onClick={save} disabled={busy} data-testid="save-profile-btn"><Save className="h-4 w-4" /> Kaydet</Button>
          <Button onClick={genAI} disabled={busy || !s.short_intro} variant="outline" data-testid="ai-generate-btn"><Sparkles className="h-4 w-4" /> AI ile Detaylandır</Button>
        </div>
      </div>
      {s.ai_generated_content && (
        <div className="rounded-xl border border-accent/30 bg-accent/5 p-6">
          <div className="overline text-accent mb-2 flex items-center gap-1"><Sparkles className="h-3 w-3" /> AI Tarafından Üretilen İçerik</div>
          <div className="text-sm whitespace-pre-line leading-relaxed">{s.ai_generated_content}</div>
        </div>
      )}
    </div>
  );
}
